import React, { useState } from 'react';
import { CrownIcon, ZapIcon, CheckIcon, MessageCircleIcon, MousePointerClickIcon, PhoneIcon, BarChart2Icon, MapPinIcon, BuildingIcon, ChevronDownIcon, WalletIcon, TrendingUpIcon, SettingsIcon, PlusIcon } from 'lucide-react';
import { useBillingCart } from '../../context/BillingCartContext';
import { FloatingCart } from '../../components/billing/FloatingCart';
import { TopUpModal } from '../../components/billing/TopUpModal';
import { useViewMode } from '../../context/ViewModeContext';
// Location Data
const LOCATIONS = [{
  id: 'MH401',
  name: 'Mumbai Motors',
  city: 'Mumbai',
  leads: '20.37K',
  spend: '₹2.25M'
}, {
  id: 'DL302',
  name: 'Delhi Auto Hub',
  city: 'Delhi',
  leads: '17.15K',
  spend: '₹1.89M'
}, {
  id: 'HR407',
  name: 'Koncept Auto',
  city: 'Gurugram',
  leads: '15.28K',
  spend: '₹1.68M'
}, {
  id: 'MH201',
  name: 'Pune Motors',
  city: 'Pune',
  leads: '13.20K',
  spend: '₹1.45M'
}, {
  id: 'TN101',
  name: 'Chennai Cars',
  city: 'Chennai',
  leads: '14.85K',
  spend: '₹1.52M'
}];
// Pricing Data
const PLANS = [{
  id: 'basic',
  name: 'Basic',
  price: 2999,
  features: ['Core Lead Management', 'Basic Analytics', '1 User']
}, {
  id: 'pro',
  name: 'Pro',
  price: 4999,
  features: ['Advanced Analytics', '5 Users', 'WhatsApp Integration', 'Workflows']
}, {
  id: 'enterprise',
  name: 'Enterprise',
  price: 7999,
  features: ['All Pro Features', 'AI Voice Agents', 'Dedicated Support', 'Custom Integrations']
}];
const ADDONS = [{
  id: 'whatsapp',
  name: 'WhatsApp',
  icon: MessageCircleIcon,
  tiers: [{
    id: 'wa-starter',
    name: 'Starter',
    price: 499,
    limit: '500 msgs'
  }, {
    id: 'wa-pro',
    name: 'Professional',
    price: 1299,
    limit: '2,000 msgs'
  }, {
    id: 'wa-ent',
    name: 'Enterprise',
    price: 3999,
    limit: '10,000 msgs'
  }]
}, {
  id: 'ctwa',
  name: 'CTWA Ads',
  icon: MousePointerClickIcon,
  tiers: [{
    id: 'ctwa-starter',
    name: 'Starter',
    price: 799,
    limit: '1,000 clicks'
  }, {
    id: 'ctwa-pro',
    name: 'Professional',
    price: 2499,
    limit: '5,000 clicks'
  }, {
    id: 'ctwa-ent',
    name: 'Enterprise',
    price: 5999,
    limit: 'Unlimited'
  }]
}, {
  id: 'voice',
  name: 'AI Voice',
  icon: PhoneIcon,
  tiers: [{
    id: 'voice-in',
    name: 'Inbound',
    price: 1999,
    limit: '500 calls'
  }, {
    id: 'voice-out',
    name: 'Outbound',
    price: 2499,
    limit: '500 calls'
  }, {
    id: 'voice-both',
    name: 'Both',
    price: 3999,
    limit: '500 each'
  }]
}, {
  id: 'analysis',
  name: 'Call Analysis',
  icon: BarChart2Icon,
  tiers: [{
    id: 'ca-basic',
    name: 'Basic',
    price: 499,
    limit: '100 calls'
  }, {
    id: 'ca-pro',
    name: 'Professional',
    price: 1299,
    limit: '500 calls'
  }, {
    id: 'ca-ent',
    name: 'Enterprise',
    price: 2999,
    limit: 'Unlimited'
  }]
}];
export function Billing() {
  const {
    addItem,
    items,
    walletBalance,
    setWalletBalance
  } = useBillingCart();
  const {
    viewMode
  } = useViewMode();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Record<string, string>>({});
  const [selectedLocations, setSelectedLocations] = useState<string[]>(['all']);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const currentPlan = 'pro';
  const handleSelectPlan = (planId: string) => {
    if (planId === currentPlan) return;
    const plan = PLANS.find(p => p.id === planId);
    if (!plan) return;
    setSelectedPlan(planId);
    addItem({
      id: `plan-${planId}`,
      type: 'subscription',
      name: `${plan.name} Plan`,
      description: 'Monthly subscription',
      price: plan.price
    });
  };
  const handleSelectAddon = (addonId: string, tierId: string) => {
    const addon = ADDONS.find(a => a.id === addonId);
    const tier = addon?.tiers.find(t => t.id === tierId);
    if (!addon || !tier) return;
    setSelectedAddons(prev => ({
      ...prev,
      [addonId]: tierId
    }));
    addItem({
      id: tierId,
      type: 'addon',
      name: addon.name,
      description: `${tier.name} tier`,
      price: tier.price,
      details: tier.limit
    });
  };
  const handleLocationToggle = (locationId: string) => {
    if (locationId === 'all') {
      setSelectedLocations(['all']);
    } else {
      setSelectedLocations(prev => {
        const withoutAll = prev.filter(id => id !== 'all');
        if (withoutAll.includes(locationId)) {
          const newSelection = withoutAll.filter(id => id !== locationId);
          return newSelection.length === 0 ? ['all'] : newSelection;
        } else {
          return [...withoutAll, locationId];
        }
      });
    }
  };
  const handleTopUpConfirm = (amount: number) => {
    setWalletBalance(walletBalance + amount);
    setIsTopUpModalOpen(false);
  };
  const isAllSelected = selectedLocations.includes('all');
  const selectedLocationCount = isAllSelected ? LOCATIONS.length : selectedLocations.length;
  const isInCart = (id: string) => items.some(item => item.id === id);
  return <div className="space-y-8 pb-24" data-id="element-3396">
      <div data-id="element-3397">
        <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3398">
          Billing & Subscription
        </h1>
        <p className="text-gray-500" data-id="element-3399">
          Select what you need and pay together or separately
        </p>
      </div>

      {/* Section 1: Media Wallet */}
      <section data-id="element-3400">
        <div className="flex items-center gap-2 mb-4" data-id="element-3401">
          <WalletIcon className="text-purple-500" size={20} data-id="element-3402" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-3403">Media Wallet</h2>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-3404">
          <div className="flex items-start justify-between mb-6" data-id="element-3405">
            <div data-id="element-3406">
              <p className="text-sm text-gray-500 mb-1" data-id="element-3407">Available Balance</p>
              <h3 className="text-3xl font-bold text-gray-900" data-id="element-3408">
                ₹
                {walletBalance.toLocaleString('en-IN', {
                minimumFractionDigits: 2
              })}
              </h3>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-3409">
              <TrendingUpIcon size={14} data-id="element-3410" />
              Auto-refill active
            </div>
          </div>

          <div className="mb-6" data-id="element-3411">
            <div className="flex justify-between text-sm mb-2" data-id="element-3412">
              <span className="text-gray-600" data-id="element-3413">Monthly Spend</span>
              <span className="font-medium text-gray-900" data-id="element-3414">
                ₹8,500 / ₹15,000
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden" data-id="element-3415">
              <div className="bg-purple-500 h-full rounded-full transition-all duration-500" style={{
              width: '57%'
            }} data-id="element-3416" />
            </div>
            <p className="text-xs text-gray-500 mt-1.5 text-right" data-id="element-3417">
              57% of monthly limit used
            </p>
          </div>

          <div className="flex gap-3" data-id="element-3418">
            <button onClick={() => setIsTopUpModalOpen(true)} className="flex-1 bg-purple-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2" data-id="element-3419">
              <PlusIcon size={16} data-id="element-3420" />
              Add Funds
            </button>
            <button className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3421">
              <SettingsIcon size={18} data-id="element-3422" />
            </button>
          </div>
        </div>
      </section>

      {/* Section 2: Subscription Plans */}
      <section data-id="element-3423">
        <div className="flex items-center gap-2 mb-4" data-id="element-3424">
          <CrownIcon className="text-amber-500" size={20} data-id="element-3425" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-3426">
            Subscription Plan
          </h2>
          <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full" data-id="element-3427">
            Current: Pro
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4" data-id="element-3428">
          {PLANS.map(plan => {
          const isCurrent = plan.id === currentPlan;
          const isSelected = selectedPlan === plan.id || isInCart(`plan-${plan.id}`);
          return <div key={plan.id} className={`relative rounded-xl border-2 p-5 transition-all cursor-pointer ${isCurrent ? 'border-blue-500 bg-blue-50/50' : isSelected ? 'border-green-500 bg-green-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} onClick={() => !isCurrent && handleSelectPlan(plan.id)} data-id="element-3429">
                {isCurrent && <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded uppercase" data-id="element-3430">
                    Current
                  </span>}
                {isSelected && !isCurrent && <span className="absolute -top-2.5 right-4 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded flex items-center gap-1" data-id="element-3431">
                    <CheckIcon size={10} data-id="element-3432" /> In Cart
                  </span>}

                <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-3433">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4" data-id="element-3434">
                  <span className="text-2xl font-bold text-gray-900" data-id="element-3435">
                    ₹{plan.price.toLocaleString()}
                  </span>
                  <span className="text-sm text-gray-500" data-id="element-3436">/mo</span>
                </div>

                <ul className="space-y-2" data-id="element-3437">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3438">
                      <CheckIcon size={14} className="text-green-500" data-id="element-3439" />
                      {feature}
                    </li>)}
                </ul>

                {!isCurrent && <button className={`mt-4 w-full py-2 rounded-lg text-sm font-medium transition-colors ${isSelected ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3440">
                    {isSelected ? 'Added to Cart' : 'Select Plan'}
                  </button>}
              </div>;
        })}
        </div>
      </section>

      {/* Section 3: Locations Filter (Brand View Only) */}
      {viewMode === 'brand' && <section data-id="element-3441">
          <div className="flex items-center gap-2 mb-4" data-id="element-3442">
            <MapPinIcon className="text-emerald-500" size={20} data-id="element-3443" />
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-3444">Locations</h2>
            <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full" data-id="element-3445">
              {selectedLocationCount} of {LOCATIONS.length} selected
            </span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5" data-id="element-3446">
            <p className="text-sm text-gray-600 mb-4" data-id="element-3447">
              Select locations to view billing and subscription details
            </p>

            {/* All Locations Toggle */}
            <div onClick={() => handleLocationToggle('all')} className={`flex items-center justify-between p-4 rounded-lg border-2 mb-4 cursor-pointer transition-all ${isAllSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}`} data-id="element-3448">
              <div className="flex items-center gap-3" data-id="element-3449">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isAllSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`} data-id="element-3450">
                  {isAllSelected && <CheckIcon size={14} className="text-white" data-id="element-3451" />}
                </div>
                <div data-id="element-3452">
                  <p className="font-semibold text-gray-900" data-id="element-3453">All Locations</p>
                  <p className="text-xs text-gray-500" data-id="element-3454">
                    View aggregated data across all {LOCATIONS.length} locations
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-3455">
                <p className="text-sm font-bold text-gray-900" data-id="element-3456">₹11.25M</p>
                <p className="text-xs text-gray-500" data-id="element-3457">Total Spend</p>
              </div>
            </div>

            {/* Individual Locations */}
            <div className="relative" data-id="element-3458">
              <button onClick={() => setShowLocationDropdown(!showLocationDropdown)} className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3459">
                <span className="flex items-center gap-2" data-id="element-3460">
                  <BuildingIcon size={16} className="text-gray-400" data-id="element-3461" />
                  {isAllSelected ? 'Or select specific locations...' : `${selectedLocations.length} location(s) selected`}
                </span>
                <ChevronDownIcon size={16} className={`text-gray-400 transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} data-id="element-3462" />
              </button>

              {showLocationDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl z-10 overflow-hidden" data-id="element-3463">
                  <div className="max-h-72 overflow-y-auto" data-id="element-3464">
                    {LOCATIONS.map(location => {
                const isSelected = !isAllSelected && selectedLocations.includes(location.id);
                return <div key={location.id} onClick={() => handleLocationToggle(location.id)} className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-gray-100 last:border-0 ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}`} data-id="element-3465">
                          <div className="flex items-center gap-3" data-id="element-3466">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`} data-id="element-3467">
                              {isSelected && <CheckIcon size={10} className="text-white" data-id="element-3468" />}
                            </div>
                            <div data-id="element-3469">
                              <p className="text-sm font-medium text-gray-900" data-id="element-3470">
                                {location.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-3471">
                                {location.id} • {location.city}
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-3472">
                            <p className="text-sm font-semibold text-gray-900" data-id="element-3473">
                              {location.spend}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-3474">
                              {location.leads} leads
                            </p>
                          </div>
                        </div>;
              })}
                  </div>
                  <div className="p-3 border-t border-gray-100 bg-gray-50" data-id="element-3475">
                    <button onClick={() => setShowLocationDropdown(false)} className="w-full py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors" data-id="element-3476">
                      Apply Selection
                    </button>
                  </div>
                </div>}
            </div>

            {/* Selected Locations Summary */}
            {!isAllSelected && selectedLocations.length > 0 && <div className="mt-4 flex flex-wrap gap-2" data-id="element-3477">
                {selectedLocations.map(locId => {
            const location = LOCATIONS.find(l => l.id === locId);
            if (!location) return null;
            return <span key={locId} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium" data-id="element-3478">
                      <MapPinIcon size={12} data-id="element-3479" />
                      {location.name}
                      <button onClick={e => {
                e.stopPropagation();
                handleLocationToggle(locId);
              }} className="ml-1 hover:text-emerald-900" data-id="element-3480">
                        ×
                      </button>
                    </span>;
          })}
              </div>}
          </div>
        </section>}

      {/* Section 4: Add-on Services */}
      <section data-id="element-3481">
        <div className="flex items-center gap-2 mb-4" data-id="element-3482">
          <ZapIcon className="text-purple-500" size={20} data-id="element-3483" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-3484">
            Add-on Services
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-3485">
          {ADDONS.map(addon => {
          const Icon = addon.icon;
          return <div key={addon.id} className="bg-white rounded-xl border border-gray-200 p-5" data-id="element-3486">
                <div className="flex items-center gap-3 mb-4" data-id="element-3487">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center" data-id="element-3488">
                    <Icon className="text-purple-600" size={20} data-id="element-3489" />
                  </div>
                  <h3 className="font-semibold text-gray-900" data-id="element-3490">{addon.name}</h3>
                </div>

                <div className="space-y-2" data-id="element-3491">
                  {addon.tiers.map(tier => {
                const isSelected = selectedAddons[addon.id] === tier.id || isInCart(tier.id);
                return <button key={tier.id} onClick={() => handleSelectAddon(addon.id, tier.id)} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-purple-300'}`} data-id="element-3492">
                        <div className="text-left" data-id="element-3493">
                          <p className="text-sm font-medium text-gray-900" data-id="element-3494">
                            {tier.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-3495">{tier.limit}</p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-3496">
                          <span className="text-sm font-bold text-gray-900" data-id="element-3497">
                            ₹{tier.price}
                          </span>
                          {isSelected && <CheckIcon size={16} className="text-green-500" data-id="element-3498" />}
                        </div>
                      </button>;
              })}
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* Floating Cart - Only for Store view */}
      <FloatingCart data-id="element-3499" />

      {/* Top-Up Modal */}
      <TopUpModal isOpen={isTopUpModalOpen} onClose={() => setIsTopUpModalOpen(false)} currentBalance={walletBalance} onConfirm={handleTopUpConfirm} data-id="element-3500" />
    </div>;
}